import {paymentApiUrlFor} from '@/costregistration/paymentApiUrl.js';
import {language} from '@/shared/localize/moment.js';
import {localize} from '@/shared/localize/tcg-localize-behavior';
import {refusalMessage} from '@/shared/utils/enrollmentValidationResult.js';
import {showErrorToast, showSuccessToast} from '@/shared/utils/toasts';

export const VALIDATE_ENROLLMENTHOOK = 'VALIDATE_ENROLLMENTHOOK';
export const BUY = 'BUY';
export const CUSTOM_BUY = 'CUSTOM_BUY';
export const COMPLETE_PAYMENT = 'COMPLETE_PAYMENT';

export const CLOSE_DIALOG = 'CLOSE_DIALOG';

/** The query parameter with the reference the leerplatform puts on the closeUrl of a classroom payment at CC. */
export const PAYMENT_RETURN_PARAMETER = 'ccReturn';

const readBody = (response) => response.json().catch(() => null);

/** The text of a refused payment hook call; the caller always buys for themself. */
const refusalText = (body) => {
    const refusal = refusalMessage(body, {self: true});

    return refusal
        ? localize(refusal.key, ...refusal.params)
        : localize('aProblemHasOccuredContactTheAdministrator');
};

export const validateEnrollmentHook =
    (targetId, catalogItemSubId, targetType) => async (dispatch, getState) => {
        const paymentApiUrl = paymentApiUrlFor(getState().minervaSettings, {
            targetType,
            planningId: catalogItemSubId,
        });

        if (!paymentApiUrl) return;

        const user = getState().oidc.user;

        const idString = user.profile.idString;

        dispatch({
            type: VALIDATE_ENROLLMENTHOOK,
            async payload() {
                const res = await fetch(
                    `${paymentApiUrl}/api/1.0/enrollmenthook/validate?targetId=${targetId}&idString=${idString}&subId=${catalogItemSubId}`,
                    {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${user.access_token}`,
                        },
                    },
                );

                const data = res.ok
                    ? await res.json()
                    : {
                          isValid: false,
                          price: null,
                          expires: null,
                          paymentInfo: null,
                          message: refusalText(await readBody(res)),
                          message2: null,
                      };

                return {
                    ...data,
                    targetId,
                    catalogItemSubId,
                    targetType,
                };
            },
        });
    };

/**
 * Starts the payment of the item the costregistration state was validated for, on the host of that item. A refusal is
 * shown and changes nothing, also `failedPaymentNoPage` when costregistration has no payment page. Without a
 * `paymentUrl` there is nothing to pay and the state is validated again.
 */
export const buy = (targetId, catalogItemSubId) => async (dispatch, getState) => {
    const paymentApiUrl = paymentApiUrlFor(getState().minervaSettings, {
        targetType: getState().costregistration.targetType,
        planningId: catalogItemSubId,
    });

    if (!paymentApiUrl) return;

    const user = getState().oidc.user;

    const idString = user.profile.idString;

    dispatch({
        type: BUY,
        async payload() {
            // eslint-disable-next-line no-unused-vars
            const baseUrl = `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`;
            // var closeUrl = encodeURIComponent(`${baseUrl}/costregistration-close-dialog`);
            // var closeUrl = `${baseUrl}/costregistration-close-dialog`;

            const closeUrl = `${window.location}`;

            const res = await fetch(`${paymentApiUrl}/api/1.0/enrollmenthook/buy`, {
                method: 'POST',
                body: JSON.stringify({
                    targetId: targetId,
                    idString: idString,
                    subId: catalogItemSubId,
                    closeUrl: closeUrl,
                    language,
                }),
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user.access_token}`,
                },
            });

            if (!res.ok) {
                showErrorToast(null, refusalText(await readBody(res)));

                return {targetId, catalogItemSubId};
            }

            const {paymentUrl} = await res.json();

            if (!paymentUrl) {
                return {paymentUrl: null, targetId, catalogItemSubId, reload: true};
            }

            return {
                paymentUrl,
                targetId,
                catalogItemSubId,
            };
        },
    });
};

/**
 * Completes the payment CompetenceCredits sent the browser back from, when the page has `ccReturn` in its query: always
 * on the leerplatform, the only one that issues the reference. The parameter is removed before the request, so a
 * reference is sent once per return, also when the app asks again while the request runs.
 */
export const completePaymentReturn = () => async (dispatch, getState) => {
    const settings = getState().minervaSettings;
    const user = getState().oidc?.user;

    if (!settings?.apiUrl || !user?.access_token) return;

    const url = new URL(window.location.href);
    const reference = url.searchParams.get(PAYMENT_RETURN_PARAMETER);

    if (!reference) return;

    url.searchParams.delete(PAYMENT_RETURN_PARAMETER);
    window.history.replaceState(window.history.state, '', url);

    await dispatch({
        type: COMPLETE_PAYMENT,
        async payload() {
            const res = await fetch(`${settings.apiUrl}/api/1.0/enrollmenthook/complete`, {
                method: 'POST',
                body: JSON.stringify({reference}),
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user.access_token}`,
                },
            }).catch(() => null);

            if (res?.ok) {
                showSuccessToast(null, localize('paymentCompleted'));
            } else {
                showErrorToast(null, refusalText(res && (await readBody(res))));
            }

            return {reload: true};
        },
    });
};

export const customBuy = (paymentUrl) => async (dispatch) => {
    dispatch({
        type: CUSTOM_BUY,

        async payload() {
            return {
                paymentUrl,
            };
        },
    });
};

export const closeDialog = () => async (dispatch, getState) => {
    const targetId = getState().targetId;
    const catalogItemSubId = getState().catalogItemSubId;

    await dispatch({
        type: CLOSE_DIALOG,
        async payload() {
            return {
                targetId,
                catalogItemSubId,
            };
        },
    });
};
